import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MailOpen, Trash2, LogOut, ChevronLeft, Inbox, BarChart3, Eye, Newspaper, Plus, Pencil, Save, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = localStorage.getItem('admin_token');
  return { Authorization: `Bearer ${token}` };
}

const CATEGORIES = ['Financial Results', 'Investments', 'ESG', 'Governance', 'Press Release', 'General'];

function NewsForm({ article, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    content: article?.content || '',
    category: article?.category || 'General',
    date: article?.date || new Date().toISOString().split('T')[0],
    image_url: article?.image_url || '',
    published: article?.published ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (article?.article_id) {
        await axios.put(`${API}/api/admin/news/${article.article_id}`, form, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/api/admin/news`, form, { headers: getAuthHeaders() });
      }
      onSave();
    } catch (err) {
      alert('Error saving article');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="news-form">
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Title *</label>
        <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="news-title-input" className="w-full px-4 py-2.5 border border-slate-200 focus:border-[#0A192F] outline-none text-sm" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Excerpt *</label>
        <input type="text" value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} required data-testid="news-excerpt-input" className="w-full px-4 py-2.5 border border-slate-200 focus:border-[#0A192F] outline-none text-sm" />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Content *</label>
        <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} required rows={8} data-testid="news-content-input" className="w-full px-4 py-2.5 border border-slate-200 focus:border-[#0A192F] outline-none text-sm resize-none" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Category</label>
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="news-category-select" className="w-full px-4 py-2.5 border border-slate-200 focus:border-[#0A192F] outline-none text-sm bg-white">
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Date</label>
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="news-date-input" className="w-full px-4 py-2.5 border border-slate-200 focus:border-[#0A192F] outline-none text-sm" />
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">Image URL</label>
        <input type="text" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." data-testid="news-image-input" className="w-full px-4 py-2.5 border border-slate-200 focus:border-[#0A192F] outline-none text-sm" />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={form.published} onChange={e => setForm({ ...form, published: e.target.checked })} id="published" data-testid="news-published-check" className="accent-[#0A192F]" />
        <label htmlFor="published" className="text-sm text-slate-600">Published</label>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} data-testid="news-save-btn" className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#0A192F] text-white text-sm uppercase tracking-wider hover:bg-[#112240] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : article?.article_id ? 'Update' : 'Create'}
        </button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 px-6 py-2.5 border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
          <X className="w-4 h-4" />
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [news, setNews] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('messages');
  const [newsMode, setNewsMode] = useState('list'); // list | create | edit
  const [editingArticle, setEditingArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, messagesRes, newsRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/admin/messages`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/admin/news`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setMessages(messagesRes.data);
      setNews(newsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const openMessage = async (msg) => {
    try {
      const { data } = await axios.get(`${API}/api/admin/messages/${msg.contact_id}`, { headers: getAuthHeaders() });
      setSelectedMessage(data);
      setMessages(prev => prev.map(m => m.contact_id === msg.contact_id ? { ...m, read: true } : m));
      if (stats) setStats(prev => ({ ...prev, unread_messages: Math.max(0, prev.unread_messages - (msg.read ? 0 : 1)) }));
    } catch {}
  };

  const deleteMessage = async (contactId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/api/admin/messages/${contactId}`, { headers: getAuthHeaders() });
      setMessages(prev => prev.filter(m => m.contact_id !== contactId));
      if (selectedMessage?.contact_id === contactId) setSelectedMessage(null);
      setStats(prev => ({ ...prev, total_messages: prev.total_messages - 1 }));
    } catch {}
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/api/admin/news/${articleId}`, { headers: getAuthHeaders() });
      setNews(prev => prev.filter(n => n.article_id !== articleId));
      setStats(prev => ({ ...prev, total_news: prev.total_news - 1 }));
    } catch {}
  };

  const handleNewsSaved = () => {
    setNewsMode('list');
    setEditingArticle(null);
    fetchData();
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatDateTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg tracking-[0.2em] font-light">BNDV</h1>
            <p className="text-[10px] tracking-[0.15em] text-slate-400">ADMIN PANEL</p>
          </div>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="admin-stats">
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Inbox className="w-5 h-5 text-[#0A192F]" />
                <span className="text-xs uppercase tracking-wider text-slate-500">Total Messages</span>
              </div>
              <p className="text-3xl font-light text-[#0A192F]" data-testid="stat-total-messages">{stats.total_messages}</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-5 h-5 text-amber-600" />
                <span className="text-xs uppercase tracking-wider text-slate-500">Unread</span>
              </div>
              <p className="text-3xl font-light text-amber-600" data-testid="stat-unread-messages">{stats.unread_messages}</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <Newspaper className="w-5 h-5 text-[#0A192F]" />
                <span className="text-xs uppercase tracking-wider text-slate-500">Published News</span>
              </div>
              <p className="text-3xl font-light text-[#0A192F]" data-testid="stat-total-news">{stats.total_news}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 mb-0 border-b border-slate-200">
          <button onClick={() => { setActiveTab('messages'); setSelectedMessage(null); }} data-testid="tab-messages" className={`px-6 py-3 text-sm uppercase tracking-wider border-b-2 transition-colors ${activeTab === 'messages' ? 'border-[#0A192F] text-[#0A192F] font-medium' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            Messages
          </button>
          <button onClick={() => { setActiveTab('news'); setNewsMode('list'); setEditingArticle(null); }} data-testid="tab-news" className={`px-6 py-3 text-sm uppercase tracking-wider border-b-2 transition-colors ${activeTab === 'news' ? 'border-[#0A192F] text-[#0A192F] font-medium' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            News
          </button>
        </div>

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="bg-white border border-slate-200 border-t-0">
            {selectedMessage ? (
              <div className="p-6" data-testid="message-detail">
                <button onClick={() => setSelectedMessage(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A192F] mb-6 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                  Back to list
                </button>
                <div className="max-w-2xl">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-xl text-[#0A192F] font-medium">{selectedMessage.subject}</h3>
                      <p className="text-sm text-slate-500 mt-1">{formatDateTime(selectedMessage.created_at)}</p>
                    </div>
                    <button onClick={() => deleteMessage(selectedMessage.contact_id)} className="text-red-400 hover:text-red-600 transition-colors" data-testid="message-delete-btn">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 border border-slate-100">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Name</p>
                      <p className="text-sm text-[#0A192F]">{selectedMessage.name}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Email</p>
                      <a href={`mailto:${selectedMessage.email}`} className="text-sm text-blue-600 hover:underline">{selectedMessage.email}</a>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Company</p>
                      <p className="text-sm text-[#0A192F]">{selectedMessage.company || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Status</p>
                      <span className={`inline-flex px-2 py-0.5 text-xs uppercase tracking-wider ${selectedMessage.read ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'}`}>
                        {selectedMessage.read ? 'Read' : 'New'}
                      </span>
                    </div>
                  </div>
                  <div style={{ borderLeft: '3px solid #0A192F' }} className="pl-4">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Message</p>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="p-12 text-center">
                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No messages yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="messages-list">
                {messages.map((msg) => (
                  <div key={msg.contact_id} className={`flex items-center gap-4 px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors ${!msg.read ? 'bg-blue-50/30' : ''}`} onClick={() => openMessage(msg)} data-testid={`message-row-${msg.contact_id}`}>
                    <div className="flex-shrink-0">
                      {msg.read ? <MailOpen className="w-5 h-5 text-slate-300" /> : <Mail className="w-5 h-5 text-amber-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm truncate ${!msg.read ? 'font-medium text-[#0A192F]' : 'text-slate-700'}`}>{msg.name}</span>
                        {msg.company && <span className="text-xs text-slate-400 hidden sm:inline">({msg.company})</span>}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{msg.subject} - {msg.message.substring(0, 80)}...</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-slate-400 hidden sm:block">{formatDateTime(msg.created_at)}</span>
                      <button onClick={(e) => { e.stopPropagation(); openMessage(msg); }} className="text-slate-400 hover:text-[#0A192F] transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteMessage(msg.contact_id); }} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-msg-${msg.contact_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && (
          <div className="bg-white border border-slate-200 border-t-0">
            {newsMode === 'list' ? (
              <>
                <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
                  <span className="text-sm text-slate-500">{news.length} articles</span>
                  <button onClick={() => { setNewsMode('create'); setEditingArticle(null); }} data-testid="news-create-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A192F] text-white text-xs uppercase tracking-wider hover:bg-[#112240] transition-colors">
                    <Plus className="w-4 h-4" />
                    New Article
                  </button>
                </div>
                {news.length === 0 ? (
                  <div className="p-12 text-center">
                    <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No articles yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100" data-testid="news-list">
                    {news.map((article) => (
                      <div key={article.article_id} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`news-row-${article.article_id}`}>
                        {article.image_url && (
                          <img src={article.image_url} alt="" className="w-16 h-12 object-cover flex-shrink-0 hidden sm:block" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#0A192F] truncate">{article.title}</span>
                            {!article.published && <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500">Draft</span>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{article.category} · {formatDate(article.date)}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => { setEditingArticle(article); setNewsMode('edit'); }} className="text-slate-400 hover:text-[#0A192F] transition-colors p-1" data-testid={`edit-news-${article.article_id}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteArticle(article.article_id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" data-testid={`delete-news-${article.article_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="p-6">
                <button onClick={() => { setNewsMode('list'); setEditingArticle(null); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A192F] mb-6 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                  Back to list
                </button>
                <h3 className="text-lg text-[#0A192F] font-medium mb-6">{newsMode === 'edit' ? 'Edit Article' : 'New Article'}</h3>
                <div className="max-w-2xl">
                  <NewsForm article={editingArticle} onSave={handleNewsSaved} onCancel={() => { setNewsMode('list'); setEditingArticle(null); }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
